"use client";

import React, { FunctionComponent } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { CountrySelectorChildrenProps } from "~/bff/components/CountrySelectorChildren";
import { Image } from "~/components/image/component";
import { CountryCode } from "~/helpers/country/types";
import { prepareTextAttribute } from "~/helpers/prepare-text-attribute";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Close24 } from "~/theme/icons/components/close";
import { getImageSrc } from "~/utils/image/get-image-src";

import { COUNTRY_SELECTOR_POP_UP_CLASSES } from "./constants";
import { CountrySelectorPopupContainer } from "./styled";

export interface CountrySelectorPopupProps {
  value?: string;
  title?: string;
  description?: string;
  isOpen?: boolean;
  className?: string;
  onClick?: (countryCode?: CountryCode, countryLanguageCode?: string) => void;
  onClose?: (value: boolean) => void;
  countries?: (CountrySelectorChildrenProps | undefined)[];
}

export const CountrySelectorPopup: FunctionComponent<CountrySelectorPopupProps> = ({
  title,
  description,
  isOpen,
  onClick,
  onClose,
  countries,
  className,
}) => {
  const CUSTOM_BREAK_POINT_MOBILE = "(max-width:700px)";

  const titleId = "country-selector-popup";
  const isMobile = useMediaQuery(CUSTOM_BREAK_POINT_MOBILE);

  return (
    <CountrySelectorPopupContainer
      className={className}
      anchor={isMobile ? "bottom" : "right"}
      open={isOpen}
      onClose={() => onClose?.(false)}
      PaperProps={{
        className: COUNTRY_SELECTOR_POP_UP_CLASSES.popup,
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": titleId,
      }}
      {...getTestAutomationProps(`country-selector-popup`)}
    >
      <IconButton
        className={COUNTRY_SELECTOR_POP_UP_CLASSES.closeButton}
        color="inherit"
        aria-label="close"
        // autoFocus doesn't work in dev mode, but it works properly in production
        // https://github.com/mui/material-ui/issues/33004#issuecomment-2589862600
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onClick={() => onClose?.(false)}
        {...getTestAutomationProps("country-selector-popup-close-button")}
        size="large"
      >
        <Close24 />
      </IconButton>
      <Typography
        variant="h4"
        className={COUNTRY_SELECTOR_POP_UP_CLASSES.title}
        id={titleId}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        className={COUNTRY_SELECTOR_POP_UP_CLASSES.description}
      >
        {description}
      </Typography>
      <List className={COUNTRY_SELECTOR_POP_UP_CLASSES.countryItemWrap}>
        {countries?.map((country) => {
          const { countryKey, image, link, languageCode } =
            country as typeof country & { countryKey: CountryCode };
          return (
            <MenuItem
              key={`${countryKey}-${link?.label}`}
              value={countryKey ?? undefined}
              className={COUNTRY_SELECTOR_POP_UP_CLASSES.countryItem}
              onClick={() =>
                onClick?.(countryKey ?? undefined, languageCode ?? undefined)
              }
            >
              <Box
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === "Enter") {
                    onClick?.(countryKey ?? undefined, languageCode ?? undefined);
                  }
                }}
                className={COUNTRY_SELECTOR_POP_UP_CLASSES.countryItem}
                {...getTestAutomationProps(`country-selector-${countryKey}`)}
              >
                <Box className={COUNTRY_SELECTOR_POP_UP_CLASSES.icon}>
                  <Image
                    src={getImageSrc(image?.source ?? undefined)}
                    alt={prepareTextAttribute(image?.alt, true)}
                    title={prepareTextAttribute(image?.title)}
                  />
                </Box>
                {link?.label}
              </Box>
            </MenuItem>
          );
        })}
      </List>
    </CountrySelectorPopupContainer>
  );
};
